import React, { useState, useMemo, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const STATUS_STYLES = {
  'Workshop':      { background: '#3b82f620', color: '#3b82f6' },
  'MOT':           { background: '#8b5cf620', color: '#8b5cf6' },
  'Bodyshop':      { background: '#f59e0b20', color: '#f59e0b' },
  'Tyres':         { background: '#10b98120', color: '#10b981' },
  'Sales':         { background: '#06b6d420', color: '#06b6d4' },
  'In Progress':   { background: '#6366f120', color: '#6366f1' },
  'Awaiting Parts':{ background: '#ef444420', color: '#ef4444' },
  'Archive':       { background: '#6b728020', color: '#6b7280' },
};

const Customers = ({ onOpenVehicleDetail }) => {
  const { vehicles, updateVehicle } = useApp();

  const [searchTerm, setSearchTerm]         = useState('');
  const [expandedKey, setExpandedKey]       = useState(null);
  const [addVehicleFor, setAddVehicleFor]   = useState(null);
  const [addReg, setAddReg]                 = useState('');
  const [regResult, setRegResult]           = useState(null);
  const [linkDone, setLinkDone]             = useState(false);

  // ---------- derive customer list from vehicles ----------
  const customers = useMemo(() => {
    const map = new Map();

    vehicles.forEach((vehicle, index) => {
      if (vehicle.deletedAt) return;

      const name    = (vehicle.customerName || '').trim();
      const contact = (vehicle.contact || '').trim();
      if (!name && !contact) return;

      const key = `${name.toLowerCase()}|||${contact}`;

      if (!map.has(key)) {
        map.set(key, {
          key,
          name:    name || 'Unknown Customer',
          contact,
          email:   vehicle.email   || '',
          address: vehicle.address || '',
          vehicles: [],
        });
      }

      const cust = map.get(key);
      if (!cust.email   && vehicle.email)   cust.email   = vehicle.email;
      if (!cust.address && vehicle.address) cust.address = vehicle.address;
      cust.vehicles.push({ ...vehicle, _index: index });
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [vehicles]);

  // ---------- filtered list ----------
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.contact.includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.vehicles.some(v =>
        (v.reg || '').toLowerCase().includes(q) ||
        (v.vehicle || '').toLowerCase().includes(q)
      )
    );
  }, [customers, searchTerm]);

  // ---------- lookup a reg ----------
  const handleSearchReg = useCallback(() => {
    const reg = addReg.toUpperCase().replace(/\s+/g, '');
    if (!reg) return;

    const idx = vehicles.findIndex(
      v => (v.reg || '').toUpperCase().replace(/\s+/g, '') === reg && !v.deletedAt
    );

    setRegResult(idx !== -1
      ? { found: true, vehicle: vehicles[idx], vehicleIndex: idx }
      : { found: false, reg }
    );
  }, [addReg, vehicles]);

  // ---------- link vehicle to customer ----------
  const handleLink = useCallback((customer) => {
    if (!regResult?.found) return;
    const { vehicleIndex } = regResult;

    updateVehicle(vehicleIndex, {
      customerName: customer.name,
      contact:      customer.contact,
      email:        customer.email || regResult.vehicle.email,
    });

    setLinkDone(true);
    setTimeout(() => {
      setAddVehicleFor(null);
      setAddReg('');
      setRegResult(null);
      setLinkDone(false);
    }, 1800);
  }, [regResult, updateVehicle]);

  // ---------- open vehicle detail ----------
  const handleOpenVehicle = useCallback((vehicle) => {
    const idx = vehicles.findIndex(v => v.id === vehicle.id);
    if (idx !== -1 && onOpenVehicleDetail) onOpenVehicleDetail(idx);
  }, [vehicles, onOpenVehicleDetail]);

  const handleCloseModal = useCallback(() => {
    setAddVehicleFor(null);
    setAddReg('');
    setRegResult(null);
    setLinkDone(false);
  }, []);

  const modalCustomer = customers.find(c => c.key === addVehicleFor);
  const totalActive   = vehicles.filter(v => !v.deletedAt).length;

  return (
    <div className="vehicle-inner">

      {/* ── Header ── */}
      <div className="vehicle-header">
        <div>
          <h2>Customers</h2>
          <p className="vehicle-subtitle">
            {customers.length} customer{customers.length !== 1 ? 's' : ''} &middot; {totalActive} vehicle{totalActive !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="vehicle-controls">
          <label className="search-field">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M10.5 3.75a6.75 6.75 0 1 1 0 13.5 6.75 6.75 0 0 1 0-13.5Zm0 1.5a5.25 5.25 0 1 0 0 10.5 5.25 5.25 0 0 0 0-10.5Zm7.97 11.28 3.15 3.15-1.06 1.06-3.15-3.15 1.06-1.06Z"
                fill="#4f8df3"
              />
            </svg>
            <input
              type="search"
              placeholder="Search by name, phone, email or plate…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* ── Customer cards ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {filtered.length === 0 ? (
          <div className="empty-state">
            {searchTerm
              ? 'No customers match your search.'
              : 'No customers found. Vehicles with customer info will appear here.'}
          </div>
        ) : (
          filtered.map(customer => {
            const isExpanded = expandedKey === customer.key;
            return (
              <div
                key={customer.key}
                style={{
                  background:   'var(--card-bg)',
                  borderRadius: '16px',
                  border:       '1px solid var(--border)',
                  overflow:     'hidden',
                  transition:   'box-shadow 0.2s',
                }}
              >
                {/* Row */}
                <div
                  style={{
                    display:    'flex',
                    alignItems: 'center',
                    padding:    '16px 20px',
                    cursor:     'pointer',
                    gap:        '16px',
                  }}
                  onClick={() => setExpandedKey(isExpanded ? null : customer.key)}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '50%',
                    background: 'var(--primary)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '18px', flexShrink: 0, textTransform: 'uppercase',
                  }}>
                    {customer.name.charAt(0)}
                  </div>

                  {/* Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-dark)', marginBottom: '2px' }}>
                      {customer.name}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      {customer.contact && (
                        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{customer.contact}</span>
                      )}
                      {customer.email && (
                        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{customer.email}</span>
                      )}
                    </div>
                  </div>

                  {/* Badge + chevron */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                    <div style={{
                      background: 'var(--primary)', color: '#fff',
                      borderRadius: '20px', padding: '4px 12px',
                      fontSize: '13px', fontWeight: 600,
                    }}>
                      {customer.vehicles.length} {customer.vehicles.length === 1 ? 'vehicle' : 'vehicles'}
                    </div>
                    <svg
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      style={{
                        width: '20px', height: '20px', color: 'var(--text-muted)',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        flexShrink: 0,
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {/* Expanded vehicles */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px' }}>

                    {/* Vehicle tiles */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
                      {customer.vehicles.map(vehicle => {
                        const statusStyle = STATUS_STYLES[vehicle.status] || { background: '#6b728020', color: '#6b7280' };
                        return (
                          <button
                            key={vehicle.id || vehicle.reg}
                            type="button"
                            onClick={() => handleOpenVehicle(vehicle)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 14px',
                              background: 'var(--card-bg)',
                              border: '1px solid var(--border)',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              transition: 'border-color 0.15s',
                              textAlign: 'left',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                          >
                            <span style={{
                              background: '#f5c542', color: '#000',
                              fontWeight: 700, fontSize: '13px',
                              fontFamily: 'monospace', letterSpacing: '1px',
                              padding: '3px 8px', borderRadius: '4px',
                            }}>
                              {vehicle.reg || '—'}
                            </span>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-dark)', lineHeight: 1.3 }}>
                                {vehicle.vehicle || vehicle.vehicleName || 'Unknown vehicle'}
                              </div>
                              {vehicle.status && (
                                <span style={{
                                  fontSize: '11px', fontWeight: 600,
                                  padding: '2px 6px', borderRadius: '4px',
                                  display: 'inline-block', marginTop: '3px',
                                  ...statusStyle,
                                }}>
                                  {vehicle.status}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Add vehicle button */}
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setAddVehicleFor(customer.key);
                        setRegResult(null);
                        setAddReg('');
                        setLinkDone(false);
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '2px dashed var(--primary)',
                        background: 'transparent',
                        color: 'var(--primary)',
                        fontSize: '13px', fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '6px',
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                        style={{ width: '15px', height: '15px' }}>
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add Vehicle
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── Add Vehicle Modal ── */}
      {addVehicleFor && modalCustomer && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '16px',
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              background: 'var(--card-bg)',
              borderRadius: '20px',
              padding: '28px',
              width: '100%', maxWidth: '440px',
              border: '1px solid var(--border)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 4px', color: 'var(--text-dark)', fontSize: '18px', fontWeight: 700 }}>
              Add Vehicle
            </h3>
            <p style={{ margin: '0 0 20px', color: 'var(--text-muted)', fontSize: '14px' }}>
              Link a vehicle to <strong style={{ color: 'var(--text-dark)' }}>{modalCustomer.name}</strong> by registration number.
            </p>

            {/* Input row */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="e.g. AB12 CDE"
                value={addReg}
                onChange={e => { setAddReg(e.target.value.toUpperCase()); setRegResult(null); }}
                onKeyDown={e => e.key === 'Enter' && handleSearchReg()}
                autoFocus
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--bg, var(--card-bg))',
                  color: 'var(--text-dark)',
                  fontSize: '15px', fontFamily: 'monospace',
                  fontWeight: 600, letterSpacing: '2px',
                  textTransform: 'uppercase', outline: 'none',
                }}
              />
              <button
                type="button"
                onClick={handleSearchReg}
                style={{
                  padding: '10px 18px', borderRadius: '8px',
                  background: 'var(--primary)', color: '#fff',
                  border: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Search
              </button>
            </div>

            {/* Result */}
            {regResult && !linkDone && (
              regResult.found ? (
                <div style={{
                  padding: '14px', borderRadius: '10px',
                  background: '#10b98115', border: '1px solid #10b98140',
                  marginBottom: '16px',
                }}>
                  <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Vehicle found
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{
                      background: '#f5c542', color: '#000',
                      fontWeight: 700, fontSize: '14px',
                      fontFamily: 'monospace', letterSpacing: '1px',
                      padding: '3px 8px', borderRadius: '4px',
                    }}>
                      {regResult.vehicle.reg}
                    </span>
                    <span style={{ fontSize: '13px', color: 'var(--text-dark)' }}>
                      {regResult.vehicle.vehicle || regResult.vehicle.vehicleName || 'Unknown vehicle'}
                    </span>
                  </div>
                  {regResult.vehicle.customerName && regResult.vehicle.customerName !== modalCustomer.name && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                      Currently linked to: <strong>{regResult.vehicle.customerName}</strong>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleLink(modalCustomer)}
                    style={{
                      padding: '8px 16px', borderRadius: '8px',
                      background: '#10b981', color: '#fff',
                      border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Link to {modalCustomer.name}
                  </button>
                </div>
              ) : (
                <div style={{
                  padding: '14px', borderRadius: '10px',
                  background: '#ef444415', border: '1px solid #ef444430',
                  marginBottom: '16px',
                }}>
                  <div style={{ fontSize: '12px', color: '#ef4444', fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Not found
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                    No vehicle with registration <strong style={{ fontFamily: 'monospace' }}>{regResult.reg}</strong> exists in the system. Book a new job to add it.
                  </div>
                </div>
              )
            )}

            {/* Success confirmation */}
            {linkDone && (
              <div style={{
                padding: '14px', borderRadius: '10px',
                background: '#10b98115', border: '1px solid #10b98140',
                marginBottom: '16px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '14px', color: '#10b981', fontWeight: 700 }}>
                  ✓ Vehicle linked successfully
                </div>
              </div>
            )}

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{
                  padding: '10px 20px', borderRadius: '8px',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text-muted)', fontSize: '14px', cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
